/**
 * SuperAdmin Managed Users page.
 *
 * Closes the Phase 6 UX gap: only SuperAdmins can attach Zebu shadow users
 * to an AccountManager's pool. Before this page existed, the only way to do
 * it was via curl against POST /admin/managed-users.
 *
 * Two-pane layout:
 *  - Left: list of AccountManagers (from GET /admin/super/account-managers)
 *  - Right: selected AM's managed-user pool with attach + detach actions
 *
 * The Attach dialog asks for the Zebu client_id; the backend resolves
 * the corresponding shadow user_id via find_broker_account_by_client_id.
 *
 * Guarded by the SuperAdmins Cognito group.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Home, Plus, RefreshCw, Search, Trash2, User as UserIcon, Users } from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { useToast } from '../components/common/ToastContainer';
import { useAuth } from '../context/AuthContext';
import { useActingAs } from '../context/ActingAsContext';
import ClientGroupsPanel from '../components/admin/ClientGroupsPanel';
import managedAccountsService from '../services/managedAccountsService';
import publisherGroupsService from '../services/publisherGroupsService';
import type { ManagedAccount } from '../types';
import type { AccountManagerListing } from '../types/publisherGroups';

function formatIST(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
    });
  } catch {
    return '—';
  }
}

const SuperAdminManagedUsersPage: React.FC = () => {
  const { isSuperAdmin, isLoading: authLoading } = useAuth();
  const { refresh: refreshActingAsPool } = useActingAs();
  const toast = useToast();
  // ToastContext returns a fresh object every render — capture in a ref so
  // useCallback can stay [] and the data-fetch useEffect doesn't re-fire
  // on every toast emission. Same pattern as SuperAdminGroupsPage.
  const toastRef = useRef(toast);
  useEffect(() => { toastRef.current = toast; }, [toast]);

  const [managers, setManagers] = useState<AccountManagerListing[]>([]);
  const [managersLoading, setManagersLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedManager, setSelectedManager] = useState<AccountManagerListing | null>(null);
  const [pool, setPool] = useState<ManagedAccount[]>([]);
  const [poolLoading, setPoolLoading] = useState(false);

  // Attach dialog
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachClientId, setAttachClientId] = useState('');
  const [isAttaching, setIsAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  // Detach confirm
  const [pendingDetach, setPendingDetach] = useState<ManagedAccount | null>(null);
  const [isDetaching, setIsDetaching] = useState(false);

  // Search filters — client-side over the already-loaded snapshot.
  const [managerQuery, setManagerQuery] = useState('');
  const [poolQuery, setPoolQuery] = useState('');

  const filteredManagers = useMemo(() => {
    const q = managerQuery.trim().toLowerCase();
    if (!q) return managers;
    return managers.filter((m) =>
      (m.full_name || '').toLowerCase().includes(q) ||
      (m.email || '').toLowerCase().includes(q) ||
      m.user_id.toLowerCase().includes(q),
    );
  }, [managers, managerQuery]);

  const filteredPool = useMemo(() => {
    const q = poolQuery.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter((p) =>
      (p.full_name || '').toLowerCase().includes(q) ||
      (p.email || '').toLowerCase().includes(q) ||
      p.managed_user_id.toLowerCase().includes(q) ||
      p.managed_client_id.toLowerCase().includes(q),
    );
  }, [pool, poolQuery]);

  const fetchManagers = useCallback(async () => {
    setManagersLoading(true);
    setError(null);
    try {
      const data = await publisherGroupsService.listAccountManagers();
      setManagers(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load AccountManagers');
    } finally {
      setManagersLoading(false);
    }
  }, []);

  const fetchPool = useCallback(async (managerSub: string) => {
    setPoolLoading(true);
    try {
      const data = await managedAccountsService.list({ accountManagerUserId: managerSub });
      setPool(data);
    } catch (err: any) {
      toastRef.current.showError(err.message || 'Failed to load managed pool');
    } finally {
      setPoolLoading(false);
    }
  }, []);

  // Skip fetches until auth has loaded AND caller is SuperAdmin — otherwise
  // the initial render fires a 403-bound request for non-SuperAdmin URL-hits.
  useEffect(() => {
    if (authLoading || !isSuperAdmin) return;
    fetchManagers();
  }, [fetchManagers, authLoading, isSuperAdmin]);

  useEffect(() => {
    if (authLoading || !isSuperAdmin) return;
    if (selectedManager) {
      fetchPool(selectedManager.user_id);
    } else {
      setPool([]);
    }
  }, [selectedManager, fetchPool, authLoading, isSuperAdmin]);

  const handleAttach = async () => {
    if (!selectedManager) return;
    const clientId = attachClientId.trim();
    if (!clientId) {
      setAttachError('Zebu Client ID is required');
      return;
    }
    setIsAttaching(true);
    setAttachError(null);
    try {
      await managedAccountsService.attach(selectedManager.user_id, clientId);
      toast.showSuccess(
        `Attached ${clientId} to ${selectedManager.email || selectedManager.user_id}`,
      );
      setAttachOpen(false);
      setAttachClientId('');
      // allSettled — attach already succeeded; a follow-up pool refresh
      // failure must not show an error toast that contradicts the success
      // toast and tricks the SA into retrying the attach.
      await Promise.allSettled([fetchPool(selectedManager.user_id), refreshActingAsPool()]);
    } catch (err: any) {
      setAttachError(err.message || 'Failed to attach');
    } finally {
      setIsAttaching(false);
    }
  };

  const handleDetach = async () => {
    if (!selectedManager || !pendingDetach) return;
    setIsDetaching(true);
    try {
      await managedAccountsService.detach(
        pendingDetach.managed_user_id,
        pendingDetach.managed_client_id,
        selectedManager.user_id, // SuperAdmin-scoped detach
      );
      toast.showSuccess(`Detached ${pendingDetach.managed_client_id}`);
      setPendingDetach(null);
      // allSettled — see comment above the attach refresh.
      await Promise.allSettled([fetchPool(selectedManager.user_id), refreshActingAsPool()]);
    } catch (err: any) {
      toast.showError(err.message || 'Failed to detach');
    } finally {
      setIsDetaching(false);
    }
  };

  // Auth gate: redirect non-SuperAdmins BEFORE rendering page chrome. Hooks
  // above always run (so order is stable); fetches are already gated above.
  if (authLoading) return null;
  if (!isSuperAdmin) return <Navigate to="/" replace />;

  return (
    <StandardLayout>
      <PageHeader
        title="Managed User Assignments"
        pageType="management"
        icon={<Users className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
          { label: 'Platform' },
          { label: 'Platform Owner' },
        ]}
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              fetchManagers();
              if (selectedManager) fetchPool(selectedManager.user_id);
            }}
            disabled={managersLoading}
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${managersLoading ? 'animate-spin' : ''}`} />
          </Button>
        }
      />

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <span className="text-red-800 dark:text-red-200">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left pane: AccountManagers */}
        <Card>
          <CardHeader>
            <CardTitle>
              AccountManagers ({managerQuery ? `${filteredManagers.length} of ${managers.length}` : managers.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            {managersLoading ? (
              <LoadingSpinner />
            ) : managers.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                No AccountManagers found. Add users to the AccountManagers Cognito group via the AWS console.
              </div>
            ) : (
              <>
                <div className="px-1 pb-2">
                  <Input
                    value={managerQuery}
                    onChange={(e) => setManagerQuery(e.target.value)}
                    placeholder="Search managers…"
                    leftIcon={<Search className="h-4 w-4" />}
                    size="sm"
                  />
                </div>
                {filteredManagers.length === 0 ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                    No matches.
                  </div>
                ) : (
                <ul className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
                  {filteredManagers.map((m) => {
                  const isSelected = selectedManager?.user_id === m.user_id;
                  return (
                    <li key={m.user_id}>
                      <button
                        type="button"
                        onClick={() => setSelectedManager(m)}
                        className={`w-full flex items-start gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                          isSelected
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
                        }`}
                      >
                        <UserIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div className="text-left min-w-0">
                          <div className="font-medium truncate">
                            {m.full_name || m.email || m.user_id}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {m.email || m.user_id}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Right pane: selected manager's pool */}
        <div className="lg:col-span-2">
          {selectedManager ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle>
                      Pool: {selectedManager.full_name || selectedManager.email || selectedManager.user_id} ({poolQuery ? `${filteredPool.length} of ${pool.length}` : pool.length})
                    </CardTitle>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <code>{selectedManager.user_id}</code>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      setAttachClientId('');
                      setAttachError(null);
                      setAttachOpen(true);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Attach Managed User
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {poolLoading ? (
                  <LoadingSpinner />
                ) : pool.length === 0 ? (
                  <div className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    No managed users in this pool yet. Use <strong>Attach Managed User</strong> to add one.
                  </div>
                ) : (
                  <>
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                      <Input
                        value={poolQuery}
                        onChange={(e) => setPoolQuery(e.target.value)}
                        placeholder="Search by name, email, or client ID…"
                        leftIcon={<Search className="h-4 w-4" />}
                        size="sm"
                      />
                    </div>
                    {filteredPool.length === 0 ? (
                      <div className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        No matches.
                      </div>
                    ) : (
                  <ul className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[60vh] overflow-y-auto">
                    {filteredPool.map((acct) => (
                      <li
                        key={`${acct.managed_user_id}#${acct.managed_client_id}`}
                        className="px-6 py-3 flex items-center justify-between"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {acct.full_name || acct.email || acct.managed_user_id}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            <code>{acct.managed_client_id}</code>
                            {acct.broker_name && <> · {acct.broker_name}</>}
                            {acct.granted_at && <> · granted {formatIST(acct.granted_at)}</>}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPendingDetach(acct)}
                          title="Detach"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
                <Users className="h-8 w-8 mx-auto mb-3 text-gray-400" />
                Select an AccountManager on the left to view and manage their pool.
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Client groups taxonomy — SuperAdmin owns CRUD; AMs assign + filter (Phase 2). */}
      <ClientGroupsPanel />

      {/* Attach modal */}
      <Modal
        isOpen={attachOpen}
        onClose={() => { if (!isAttaching) setAttachOpen(false); }}
        ariaLabel="Attach Managed User"
        maxWidth="md"
      >
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Attach Managed User to {selectedManager?.full_name || selectedManager?.email}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            The Zebu shadow user must have signed in via OAuth at least once
            before being attached.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Zebu Client ID <span className="text-red-500">*</span>
            </label>
            <Input
              value={attachClientId}
              onChange={(e) => setAttachClientId(e.target.value)}
              placeholder="Z71517"
              disabled={isAttaching}
            />
          </div>
          {attachError && (
            <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2">
              {attachError}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setAttachOpen(false)} disabled={isAttaching}>
              Cancel
            </Button>
            <Button onClick={handleAttach} disabled={isAttaching}>
              {isAttaching ? 'Attaching…' : 'Attach'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Detach confirm */}
      <ConfirmDialog
        isOpen={pendingDetach !== null}
        title={`Detach ${pendingDetach?.managed_client_id ?? ''}?`}
        message={`Removes this managed user from ${selectedManager?.full_name || selectedManager?.email}'s pool. The user's broker_accounts row is untouched. Existing subscriptions stay active.`}
        confirmText={isDetaching ? 'Detaching…' : 'Detach'}
        variant="danger"
        onConfirm={handleDetach}
        onClose={() => setPendingDetach(null)}
      />
    </StandardLayout>
  );
};

export default SuperAdminManagedUsersPage;
